<template>
  <div class="code-tag-editor">
    <div v-if="editHTMLObjects.length === 0" class="editor-no-content-info">
      <p>
        編集できるコンテンツはありません
      </p>
    </div>
    <div v-if="!isValidCustomAttr" class="invalid-custom-attr-info">
      <p>
        ※独自属性の指定方法に誤りがあり編集できないコンテンツがあります
      </p>
    </div>
    <div v-for="editHTML in editHTMLObjects" :key="editHTML.nodeIndex" class="editor-content">
      <template v-for="(attrValue, index) in editHTML.attrValues">
        <div :key="index">
          <p class="editor-content-title">
            <i class="editor-content-icon" :class="getIconClassName(attrValue.type)"></i>
            <span>
              {{ attrValue.labelText }}
            </span>
          </p>
          <!-- テキストエディタ -->
          <lazy-component>
            <tiny-mce-editor
              v-if="isText(attrValue.type) && renderComponent"
              class="tinymceEditor"
              :key="partId"
              :value="editHTML.innerHTML"
              :isCodePart="true"
              :sectionId="sectionId"
              :partsContainerId="partsContainerId"
              :partId="partId"
              :codePartNodeIndex="editHTML.nodeIndex"
              @change="change($event)"
            />
          </lazy-component>
          <!-- 画像 -->
          <code-image-editor
            v-if="isImage(attrValue.type)"
            :key="index"
            :current="current"
            :codePartNodeIndex="editHTML.nodeIndex"
            :src="getSrcFromUuid(editHTML.src)"
            :imageKey="editHTML.imageKey"
            :sectionId="sectionId"
            :partsContainerId="partsContainerId"
            :partId="partId"
            :part="part"
            :partsContainerType="partsContainerType"
            @change="change($event, editHTML.src)"
          />
          <!-- リンク -->
          <code-link-editor
            v-if="isLink(attrValue.type)"
            :key="index"
            :codePartNodeIndex="editHTML.nodeIndex"
            :href="getHrefFromUuid(editHTML.href)"
            :title="editHTML.title"
            :target="editHTML.target"
            :sectionId="sectionId"
            :partsContainerId="partsContainerId"
            :partsContainerType="partsContainerType"
            :partId="partId"
            @change="change($event, editHTML.href)"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import constants from '@/constants'
import { stringToHTML, getLinkExtractionBody, restoreLinkToBody } from '@/utils/String'
import TinyMceEditor from '@/components/Content/Palette/EditPartDesign/Editor/TinyMceEditor'
import CodeImageEditor from '@/components/Content/Palette/EditPartDesign/Editor/Code/CodeImageEdit'
import CodeLinkEditor from '@/components/Content/Palette/EditPartDesign/Editor/Code/CodeLinkEdit'

// コードパーツ用の独自属性名
const { textAttrName, linkAttrName, imageAttrName, labelAttrName } = constants.part.code

export default {
  props: [
    'current',
    'code',
    'sectionId',
    'partsContainerId',
    'partsContainerType',
    'partId',
    'part'
  ],
  data () {
    return {
      contentTagCode: null,
      contentTagCodeHrefs: [],
      contentTagCodeSrcs: [],
      HTMLObject: null,
      HTMLCollection: null,
      editNodeList: [],
      editHTMLObjects: [],
      renderComponent: true,
      isValidCustomAttr: true
    }
  },
  components: {
    TinyMceEditor,
    CodeImageEditor,
    CodeLinkEditor,
  },
  computed: {
    isText: function () {
      return function (type) {
        return type === 'text'
      }
    },
    isImage: function () {
      return function (type) {
        return type === 'image'
      }
    },
    isLink: function () {
      return function (type) {
        return type === 'link'
      }
    },
    getHrefFromUuid: function () {
      return function (uuid) {
        const contentTagCodeHref = this.contentTagCodeHrefs.find(href => href.uuid === uuid)
        return contentTagCodeHref.href ? contentTagCodeHref.href : ''
      }
    },
    getSrcFromUuid: function () {
      return function (uuid) {
        const contentTagCodeSrc = this.contentTagCodeSrcs.find(src => src.uuid === uuid)
        return contentTagCodeSrc.src ? contentTagCodeSrc.src : ''
      }
    },
  },
  methods: {
    initComponent () {
      [this.contentTagCodeHrefs, this.contentTagCodeSrcs] = []
      // リンクの&誤変換対策
      ;[this.contentTagCode, this.contentTagCodeHrefs, this.contentTagCodeSrcs] = getLinkExtractionBody(this.code)
      // ループで上から順にDOMを抜き出せるよう、HTMLCollectionの形で取得する
      // HTMLCollectionを変更すると、HTMLObjectも変更される
      ;[this.HTMLObject, this.HTMLCollection] = stringToHTML(this.contentTagCode)
      this.createEditNodeList()
      this.createEditHTMLObjects()
      // コードパーツ 未使用の画像を削除
      this.$store.commit('deleteCodePartUnUseImage', this.$store.getters.getSectionAll)
    },
    forceRerender () {
      // TinyMCE を再レンダする
      this.renderComponent = false
      this.$nextTick(() => {
        this.renderComponent = true
      })
    },
    getIconClassName (type) {
      let iconClassName = null
      switch (type) {
        case 'text':
          iconClassName = 'fas fa-file-alt'
          break
        case 'image':
          iconClassName = 'fas fa-file-image'
          break
        case 'link':
          iconClassName = 'fas fa-link'
          break
        default:
          iconClassName = 'fas fa-question'
      }
      return iconClassName
    },
    change (event, uuid = null) {
      if (event.type === 'text') {
        this.editNodeList[event.nodeIndex].innerHTML = event.newValue
      } else if (event.type === 'image') {
        this.setUuidToLink(event, uuid)
        if (event.key) this.editNodeList[event.nodeIndex].setAttribute('data-image-key', event.key)
        event.alt ? this.editNodeList[event.nodeIndex].setAttribute('alt', event.alt) : this.editNodeList[event.nodeIndex].removeAttribute('alt')
      } else if (event.type === 'link') {
        this.setUuidToLink(event, uuid)
        this.setLink(event, uuid)
      }

      this.createEditHTMLObjects()
      this.contentTagCode = this.HTMLObject.body.innerHTML
      // リンクの&誤変換対策
      const newBody = restoreLinkToBody(this.contentTagCode, this.contentTagCodeHrefs, this.contentTagCodeSrcs)

      this.$emit('change', newBody)
    },
    // リンクと UUID を紐づける
    setUuidToLink (values, uuid) {
      const { nodeIndex, newValue, href, type } = values
      if (!uuid) {
        uuid = Math.random().toString(32).substring(2)
      }
      if (type === 'image') {
        let contentTagCodeSrc = this.contentTagCodeSrcs.find(src => src.uuid === uuid)
        contentTagCodeSrc.src = newValue ?? ''
        this.editNodeList[nodeIndex].setAttribute('src', uuid)
      } else if (type === 'link') {
        let contentTagCodeHref = this.contentTagCodeHrefs.find(href => href.uuid === uuid)
        contentTagCodeHref.href = href ?? ''
        this.editNodeList[nodeIndex].setAttribute('href', uuid)
      }
    },
    // リンク属性をセット: 無い場合はDOMから取り除く
    setLink (values, uuid = null) {
      const { nodeIndex, href, target, title } = values
      if (href && target) {
        this.editNodeList[nodeIndex].setAttribute('target', target)
        // 入れ子処理
        this.editNodeList.forEach(nodeList => {
          if (nodeList.getElementsByTagName('a').length > 0) {
            nodeList.getElementsByTagName('a').forEach(aTag => {
              if (aTag.getAttribute('href') === uuid) {
                aTag.setAttribute('target', target)
              }
            })
          }
        })
      } else {
        this.editNodeList[nodeIndex].removeAttribute('target')
        // 入れ子処理
        this.editNodeList.forEach(nodeList => {
          if (nodeList.getElementsByTagName('a').length > 0) {
            nodeList.getElementsByTagName('a').forEach(aTag => {
              if (aTag.getAttribute('href') === uuid) {
                aTag.removeAttribute('target')
              }
            })
          }
        })
      }
      if (href && title) {
        this.editNodeList[nodeIndex].setAttribute('title', title)
        // 入れ子処理
        this.editNodeList.forEach(nodeList => {
          if (nodeList.getElementsByTagName('a').length > 0) {
            nodeList.getElementsByTagName('a').forEach(aTag => {
              if (aTag.getAttribute('href') === uuid) {
                aTag.setAttribute('title', title)
              }
            })
          }
        })
      } else {
        this.editNodeList[nodeIndex].removeAttribute('title')
        // 入れ子処理
        this.editNodeList.forEach(nodeList => {
          if (nodeList.getElementsByTagName('a').length > 0) {
            nodeList.getElementsByTagName('a').forEach(aTag => {
              if (aTag.getAttribute('href') === uuid) {
                aTag.removeAttribute('title')
              }
            })
          }
        })
      }
      this.forceRerender()
    },
    // DOM から、編集可能箇所を抜き出す
    createEditNodeList () {
      this.editNodeList = []
      if (!this.HTMLCollection) return
      for (let i = 0; i < this.HTMLCollection.length; i++) {
        const HTMLCollect = this.HTMLCollection[i]
        // 独自属性が設定されているかどうか確認する
        // labelAttrNameは確認不要
        const inputAttrs = []
        ;[textAttrName, linkAttrName, imageAttrName].forEach(attrName => {
          HTMLCollect.hasAttribute(attrName) && inputAttrs.push(attrName)
        })
        // 独自属性がなければスルーする
        if (inputAttrs.length === 0) continue
        // 独自属性が設定されていれば適切に設定されているか確認する
        if (!this.checkCustomAttr(HTMLCollect, inputAttrs)) {
          this.isValidCustomAttr = false
          continue
        }
        this.editNodeList.push(HTMLCollect)
        // &amp;への誤変換対策
        if ((HTMLCollect.hasAttribute(linkAttrName) && !HTMLCollect.hasAttribute('href')) ||
        (HTMLCollect.hasAttribute(imageAttrName) && !HTMLCollect.hasAttribute('src'))) {
          // href 指定もしくは src 指定がない場合
          const uuid = Math.random().toString(32).substring(2)
          if (HTMLCollect.hasAttribute(linkAttrName)) {
            // href
            this.contentTagCodeHrefs.push({
              uuid: uuid,
              href: '',
            })
            HTMLCollect.setAttribute('href', uuid)
          } else if (HTMLCollect.hasAttribute(imageAttrName)) {
            // src
            this.contentTagCodeSrcs.push({
              uuid: uuid,
              src: '',
            })
            HTMLCollect.setAttribute('src', uuid)
          }
        }
      }
    },
    // 独自属性が適切に設定されているか確認する
    checkCustomAttr (HTMLCollect, inputAttrs) {
      // 右の入れ子も対応 : <p data-mphe-edit="CUSTOM"><a data-mphe-link="CUSTOM" target="_top"></a></p>
      const tagName = HTMLCollect.tagName
      const checkAttr = attrs => attrs.some(attr => JSON.stringify(inputAttrs.sort()) === JSON.stringify(attr.sort()))
      switch (tagName) {
        case 'P':
          // <p>タグはdata-mphe-editのみ設定できる
          return checkAttr([[textAttrName]])
        case 'A':
          // <a>タグはdata-mphe-linkもしくはdata-mphe-link・data-mphe-editのみ設定できる
          return checkAttr([[linkAttrName], [linkAttrName, textAttrName]])
        case 'IMG':
          // <img>タグはdata-mphe-imageのみ設定できる
          return checkAttr([[imageAttrName]])
      }
    },
    // 編集可能箇所を、テキストエディタに渡すため整形する
    createEditHTMLObjects () {
      this.editHTMLObjects = []
      for (let i = 0; i < this.editNodeList.length; i++) {
        const editNode = this.editNodeList[i]
        let attrValues = []
        if (editNode.hasAttribute(textAttrName)) {
          // テキスト
          attrValues.push(this.createAttrDefaultValue(editNode, textAttrName))
        }
        if (editNode.hasAttribute(imageAttrName)) {
          // 画像
          attrValues.push(this.createAttrDefaultValue(editNode, imageAttrName))
        }
        if (editNode.hasAttribute(linkAttrName)) {
          // リンク
          attrValues.push(this.createAttrDefaultValue(editNode, linkAttrName))
        }
        const objectValue = {
          'nodeIndex': i,
          innerHTML: editNode.innerHTML,
          src: editNode.getAttribute('src') || null,
          href: editNode.getAttribute('href') || null,
          title: editNode.getAttribute('title') || null,
          target: editNode.getAttribute('target') || null,
          imageKey: editNode.getAttribute('data-image-key') || null,
          attrValues
        }
        this.editHTMLObjects.push(objectValue)
      }
    },
    // 各属性共通の値を作成
    createAttrDefaultValue (editNode, attrName) {
      let labelText = null
      let type = null
      if (editNode.hasAttribute(labelAttrName)) {
        labelText = editNode.getAttribute(labelAttrName)
      }
      switch (attrName) {
        case textAttrName:
          type = 'text'
          if (!labelText) labelText = editNode.hasAttribute(linkAttrName) ? 'リンクテキスト' : 'テキスト'
          break
        case imageAttrName:
          type = 'image'
          if (!labelText) labelText = '画像'
          break
        case linkAttrName:
          type = 'link'
          if (!labelText) labelText = 'リンク'
          break
        default:
          labelText = ''
      }
      return {
        type,
        prop: attrName,
        value: editNode.getAttribute(attrName),
        labelText,
      }
    },
  },
  mounted () {
    this.initComponent()
  },
  watch: {
    partsContainerId () {
      // コードパーツを選択した状態で、別のコードパーツを選択した際に表示を更新する
      this.initComponent()
    }
  }
}
</script>

<style scoped lang="scss">
.code-tag-editor {
  .editor-content {
    padding: 0px 0px 15px;
    border-bottom: 1px solid #707070;
    &:last-of-type {
      border-bottom: 0px solid #707070;
    }
  }
  .editor-content-icon {
    color: #666;
    margin-left: 5px;
  }
  .editor-content-title {
    padding: 15px 0 15px 5px;
    font-weight: 700;
  }
  .editor-no-content-info {
    padding: 0 10px;
  }
  .invalid-custom-attr-info {
    color: #ff0000;
    padding: 0 10px;
  }
  .code-image-edit {
    margin: 0 !important;
  }
  .code-link-edit {
    padding-left: 5px;
  }
}
</style>
